/**
 * Synthetic Data Generator
 * 
 * Generates synthetic agents, contracts, and leads for simulation.
 * All generation uses seeded RNG for deterministic, repeatable results.
 */

import type { SeededRNG } from './seededRandom';
import type {
  SyntheticAgent,
  SyntheticContract,
  SyntheticLead,
  AgentPopulationConfig,
  ContractMix,
  LeadDistribution,
} from './types';
import type { DeliveryTarget } from '@/contexts/MockAgentsContext';
import type { ContractType } from '@/contexts/ContractsContext';

// --- US States for licensing ---

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// --- Lead Types ---

const LEAD_TYPES = [
  { id: 'life_fex', name: 'Final Expense', vertical: 'life_insurance' as const },
  { id: 'life_iul', name: 'IUL', vertical: 'life_insurance' as const },
  { id: 'life_mp', name: 'Mortgage Protection', vertical: 'life_insurance' as const },
  { id: 'mortgage_purchase', name: 'Purchase', vertical: 'mortgage' as const },
  { id: 'mortgage_refi', name: 'Refinance', vertical: 'mortgage' as const },
  { id: 'mortgage_heloc', name: 'HELOC', vertical: 'mortgage' as const },
  { id: 'mortgage_reverse', name: 'Reverse Mortgage', vertical: 'mortgage' as const },
];

// --- Name Generation ---

const FIRST_NAMES = [
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Elizabeth', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Nancy', 'Daniel', 'Lisa',
];

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas',
  'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White',
];

const COMPANIES = [
  'Premier Insurance', 'National Financial', 'Alliance Group', 'Pinnacle Partners',
  'Summit Advisors', 'Liberty Financial', 'Guardian Services', 'Atlas Insurance',
  'Horizon Group', 'Apex Financial', 'Delta Partners', 'Omega Advisors',
];

// --- Generator Functions ---

export function generateSyntheticAgents(
  config: AgentPopulationConfig,
  simulationStart: number,
  simulationEnd: number,
  rng: SeededRNG
): SyntheticAgent[] {
  const agents: SyntheticAgent[] = [];
  const totalDays = Math.ceil((simulationEnd - simulationStart) / (24 * 60 * 60 * 1000));
  
  // Build tier array based on distribution
  const tiers: Array<'Bronze' | 'Silver' | 'Gold' | 'Elite'> = [];
  const tierTotal = Object.values(config.tierDistribution).reduce((a, b) => a + b, 0);
  
  for (const [tier, weight] of Object.entries(config.tierDistribution) as Array<['Bronze' | 'Silver' | 'Gold' | 'Elite', number]>) {
    const count = Math.round((weight / tierTotal) * config.count);
    for (let i = 0; i < count; i++) tiers.push(tier);
  }
  
  // Fill remaining slots
  while (tiers.length < config.count) tiers.push('Silver');
  rng.shuffle(tiers);
  
  for (let i = 0; i < config.count; i++) {
    const tier = tiers[i];
    const firstName = rng.pick(FIRST_NAMES) || 'Agent';
    const lastName = rng.pick(LAST_NAMES) || 'Smith';
    const company = rng.pick(COMPANIES) || 'Financial Group';
    const vertical = rng.randomBool() ? 'life_insurance' : 'mortgage';
    
    // License states
    const numStates = rng.randomInt(config.licensedStatesRange.min, config.licensedStatesRange.max);
    const licensedStates = rng.pickN(US_STATES, numStates);
    
    // Monthly target
    const monthlyTarget = rng.randomInt(config.monthlyTargetRange.min, config.monthlyTargetRange.max);
    
    // Guaranteed minimum (only for some agents)
    const hasGuaranteed = rng.randomBool(config.guaranteedMinimumPct / 100);
    const guaranteedMinimum = hasGuaranteed 
      ? Math.floor(monthlyTarget * rng.randomFloat(0.3, 0.6))
      : undefined;
    
    // Wallet balance
    const walletBalance = rng.randomInt(config.walletBalanceRange.min, config.walletBalanceRange.max);
    
    // Performance based on tier
    const tierPerformance: Record<string, { min: number; max: number }> = {
      Bronze: { min: 40, max: 60 },
      Silver: { min: 55, max: 75 },
      Gold: { min: 70, max: 85 },
      Elite: { min: 80, max: 98 },
    };
    const perfRange = tierPerformance[tier];
    const performanceScore = rng.randomInt(perfRange.min, perfRange.max);
    
    // Lead preferences (bid values based on tier)
    const tierBidMultiplier: Record<string, number> = {
      Bronze: 0.8,
      Silver: 1.0,
      Gold: 1.2,
      Elite: 1.4,
    };
    
    const leadPreferences = LEAD_TYPES
      .filter(lt => lt.vertical === vertical)
      .map(lt => ({
        leadTypeId: lt.id,
        leadTypeName: lt.name,
        bidValue: Math.round(rng.randomFloat(15, 40) * tierBidMultiplier[tier]),
        dailyCap: rng.randomInt(3, 15),
        monthlyCap: monthlyTarget + rng.randomInt(5, 20),
        isActive: rng.randomBool(0.85), // 85% chance active
      }));
    
    const deliveryTarget: DeliveryTarget = {
      monthlyTarget,
      deliveredThisMonth: 0,
      cycleStartDate: simulationStart,
      cycleLengthDays: totalDays,
      guaranteedMinimum,
    };
    
    agents.push({
      _synthetic: true,
      id: `sim_agent_${i.toString().padStart(4, '0')}`,
      name: `${firstName} ${lastName}`,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${company.toLowerCase().replace(/\s+/g, '')}.com`,
      company,
      vertical,
      tier,
      isActive: true,
      isDemo: false,
      isInternalUser: false,
      performanceScore,
      walletBalance,
      paidBalance: Math.floor(walletBalance * 0.8),
      promotionalBalance: Math.floor(walletBalance * 0.2),
      activeLeadTypesCount: leadPreferences.filter(p => p.isActive).length,
      licensedStates,
      leadPreferences,
      performance: {
        avgResponseTime: rng.randomFloat(5, 120),
        contactRate: rng.randomFloat(0.3, 0.9),
        closeRate: rng.randomFloat(0.1, 0.5),
      },
      leadsReceivedToday: 0,
      leadsReceivedThisMonth: 0,
      deliveryTarget,
    });
  }
  
  return agents;
}

export function generateSyntheticContracts(
  agents: SyntheticAgent[],
  config: ContractMix,
  simulationStart: number,
  simulationEnd: number,
  rng: SeededRNG
): SyntheticContract[] {
  const contracts: SyntheticContract[] = [];
  const totalContracts = config.monthlyTargetCount + config.prepaidPackageCount + config.trialCount;
  
  if (totalContracts === 0 || agents.length === 0) return contracts;
  
  // Select agents to have contracts
  const agentsWithContracts = rng.pickN(agents, Math.min(totalContracts, agents.length));
  
  // Build contract type array
  const types: ContractType[] = [];
  for (let i = 0; i < config.monthlyTargetCount; i++) types.push('monthly_target');
  for (let i = 0; i < config.prepaidPackageCount; i++) types.push('prepaid_package');
  for (let i = 0; i < config.trialCount; i++) types.push('trial');
  rng.shuffle(types);
  
  for (let i = 0; i < agentsWithContracts.length && i < types.length; i++) {
    const agent = agentsWithContracts[i];
    const contractType = types[i];
    
    const totalLeadsCommitted = rng.randomInt(
      config.leadsCommittedRange.min,
      config.leadsCommittedRange.max
    );
    
    contracts.push({
      _synthetic: true,
      id: `sim_contract_${i.toString().padStart(3, '0')}`,
      agentId: agent.id,
      agentName: agent.name,
      agentEmail: agent.email || '',
      contractType,
      totalLeadsCommitted,
      cycleStart: simulationStart,
      cycleEnd: simulationEnd,
      leadsDelivered: 0,
      status: 'active',
      priorityTier: 'balanced',
      createdAt: simulationStart,
    });
  }
  
  return contracts;
}

export function generateSyntheticLeads(
  numberOfLeads: number,
  startDate: number,
  endDate: number,
  distribution: LeadDistribution,
  rng: SeededRNG
): SyntheticLead[] {
  const leads: SyntheticLead[] = [];
  const duration = endDate - startDate;
  
  // Build weighted arrays for state selection
  const states = Object.keys(distribution.stateWeights);
  const stateWeights = Object.values(distribution.stateWeights);
  
  // If no state weights specified, use default distribution
  if (states.length === 0) {
    // High-population states get more weight
    const defaultStates = ['CA', 'TX', 'FL', 'NY', 'PA', 'IL', 'OH', 'GA', 'NC', 'MI'];
    for (const s of defaultStates) {
      states.push(s);
      stateWeights.push(1);
    }
  }
  
  // Build lead type arrays
  const leadTypeIds = Object.keys(distribution.leadTypeWeights);
  const leadTypeWeights = Object.values(distribution.leadTypeWeights);
  
  // If no lead type weights, use default
  if (leadTypeIds.length === 0) {
    for (const lt of LEAD_TYPES) {
      leadTypeIds.push(lt.id);
      leadTypeWeights.push(1);
    }
  }
  
  const leadTypeMap = new Map(LEAD_TYPES.map(lt => [lt.id, lt]));
  
  for (let i = 0; i < numberOfLeads; i++) {
    // Determine arrival time (spread across simulation period)
    // Use slight randomness but generally uniform distribution
    const baseTime = startDate + (duration * i / numberOfLeads);
    const jitter = rng.randomFloat(-duration / numberOfLeads / 2, duration / numberOfLeads / 2);
    const arrivalTime = Math.max(startDate, Math.min(endDate, baseTime + jitter));
    
    // Select lead type
    const leadTypeId = rng.weightedPick(leadTypeIds, leadTypeWeights) || LEAD_TYPES[0].id;
    const leadType = leadTypeMap.get(leadTypeId) || LEAD_TYPES[0];
    
    // Determine vertical (from lead type or weighted random)
    let vertical: 'life_insurance' | 'mortgage';
    if (leadType) {
      vertical = leadType.vertical;
    } else {
      const totalVerticalWeight = distribution.verticalWeights.life_insurance + distribution.verticalWeights.mortgage;
      vertical = rng.randomBool(distribution.verticalWeights.life_insurance / totalVerticalWeight) 
        ? 'life_insurance' 
        : 'mortgage';
    }
    
    // Select state
    const state = rng.weightedPick(states, stateWeights) || 'CA';
    
    // Generate value
    let estimatedValue: number;
    if (distribution.valueRange.mean && distribution.valueRange.stdDev) {
      // Normal distribution
      estimatedValue = Math.max(
        distribution.valueRange.min,
        Math.min(
          distribution.valueRange.max,
          rng.randomNormal(distribution.valueRange.mean, distribution.valueRange.stdDev)
        )
      );
    } else {
      // Uniform distribution
      estimatedValue = rng.randomFloat(distribution.valueRange.min, distribution.valueRange.max);
    }
    
    leads.push({
      id: `sim_lead_${i.toString().padStart(6, '0')}`,
      leadTypeId,
      leadTypeName: leadType?.name || 'Unknown',
      vertical,
      state,
      estimatedValue: Math.round(estimatedValue * 100) / 100,
      arrivalTime,
    });
  }
  
  // Sort by arrival time
  leads.sort((a, b) => a.arrivalTime - b.arrivalTime);
  
  return leads;
}

/**
 * Create default lead distribution config
 */
export function createDefaultLeadDistribution(): LeadDistribution {
  return {
    stateWeights: {
      CA: 12, TX: 10, FL: 9, NY: 8, PA: 5,
      IL: 5, OH: 4, GA: 4, NC: 4, MI: 3,
      NJ: 3, VA: 3, WA: 3, AZ: 3, MA: 2,
    },
    leadTypeWeights: {
      life_fex: 15,
      mortgage_purchase: 20,
      mortgage_refi: 10,
      mortgage_heloc: 5,
    },
    verticalWeights: {
      life_insurance: 60,
      mortgage: 40,
    },
    valueRange: {
      min: 15,
      max: 80,
      mean: 35,
      stdDev: 15,
    },
  };
}

/**
 * Create default agent population config
 */
export function createDefaultAgentPopulation(count: number = 100): AgentPopulationConfig {
  return {
    count,
    tierDistribution: {
      Bronze: 30,
      Silver: 40,
      Gold: 20,
      Elite: 10,
    },
    monthlyTargetRange: {
      min: 10,
      max: 50,
    },
    guaranteedMinimumPct: 30,
    licensedStatesRange: {
      min: 5,
      max: 25,
    },
    walletBalanceRange: {
      min: 500,
      max: 5000,
    },
  };
}

/**
 * Create default contract mix config
 */
export function createDefaultContractMix(): ContractMix {
  return {
    monthlyTargetCount: 3,
    prepaidPackageCount: 1,
    trialCount: 1,
    leadsCommittedRange: {
      min: 20,
      max: 100,
    },
    coverageRatio: 0.3, // Contracts cover 30% of total leads
  };
}
